<template>
  <div class="datasync-manage">
    <div class="page-header">
      <h2>DataSync 数据同步管理</h2>
      <p>管理AWS DataSync服务，包括创建位置、任务和监控执行状态</p>
    </div>

    <!-- 当前账户信息 -->
    <div class="payer-selector">
      <span>当前账户：</span>
      <el-tag type="primary" size="large">
        <el-icon><User /></el-icon>
        {{ selectedPayerName }} ({{ selectedPayerId }})
      </el-tag>
      <el-button type="primary" @click="syncFromAws" :loading="syncLoading" style="margin-left: 16px;">
        从AWS同步
      </el-button>
    </div>
    <div>
    <!-- 标签页 -->
    <el-tabs v-model="activeTab" type="card" class="datasync-tabs">
      <!-- 位置管理 -->
      <el-tab-pane label="位置管理" name="locations">
        <div class="tab-content">
          <div class="toolbar">
            <el-button type="primary" @click="showCreateLocationDialog">创建位置</el-button>
            <el-button @click="loadLocations">刷新</el-button>
          </div>
          
          <el-table 
            :data="locationList" 
            v-loading="locationLoading" 
            :height="'calc(100vh - 420px)'"
            style="width: 100%" 
            stripe 
            border
            :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
            :row-style="{ height: '50px' }"
            empty-text="暂无数据"

            @sort-change="handleSortChange">
            <el-table-column prop="local_alias" label="本地别名" width="160" sortable="custom">
              <template #default="scope">
                <div class="alias-cell">
                  <span v-if="!scope.row.isEditing" class="alias-text" @click="startEdit(scope.row)">
                    {{ scope.row.local_alias || '设置别名' }}
                    <el-icon class="edit-icon"><Edit /></el-icon>
                  </span>
                  <el-input
                    v-else
                    v-model="scope.row.local_alias"
                    placeholder="输入别名"
                    size="small"
                    ref="aliasInput"
                    @blur="finishEdit(scope.row)"
                    @keyup.enter="finishEdit(scope.row)"
                    clearable>
                  </el-input>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="display_name" label="位置名称" width="200" show-overflow-tooltip sortable="custom">
              <template #default="scope">
                <div style="display: flex; align-items: center;">
                  <el-icon style="margin-right: 8px; color: #409eff;"><Document /></el-icon>
                  <span style="font-weight: 500;">{{ scope.row.display_name }}</span>
                </div>
              </template>
            </el-table-column>

            <el-table-column prop="location_type" label="类型" width="150" align="center" sortable="custom">
              <template #default="scope">
                <el-tag :type="getLocationTypeColor(scope.row.location_type)" size="small">
                  {{ scope.row.location_type }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="location_uri" label="位置URI" min-width="300" show-overflow-tooltip>
              <template #default="scope">
                <el-text class="uri-text" type="info">{{ scope.row.location_uri }}</el-text>
              </template>
            </el-table-column>
            <el-table-column prop="region" label="区域" width="140" align="center">
              <template #default="scope">
                <el-tag type="info" size="small" plain>{{ scope.row.region }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="100" align="center" sortable="custom">
              <template #default="scope">
                <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'" size="small">
                  {{ scope.row.status === 1 ? '正常' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="创建时间" width="180" align="center" sortable="custom">
              <template #default="scope">
                <span style="color: #909399;">{{ scope.row.created_at || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="140" align="center" fixed="right">
              <template #default="scope">
                <el-button size="small" type="primary" link @click="viewLocationDetail(scope.row)">
                  <el-icon><View /></el-icon>
                  详情
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          
          <div class="pagination-container">
            <el-pagination
              @size-change="handleLocationSizeChange"
              @current-change="handleLocationCurrentChange"
              :current-page="locationPage"
              :page-sizes="[12, 24, 36, 50]"
              :page-size="locationSize"
              layout="total, sizes, prev, pager, next"
              :total="locationTotal"
              :background="true"
              :small="true"
              :hide-on-single-page="false">
            </el-pagination>
          </div>
        </div>
      </el-tab-pane>

      <!-- 任务管理 -->
      <el-tab-pane label="任务管理" name="tasks">
        <div class="tab-content">
          <div class="toolbar">
            <el-button type="primary" @click="showCreateTaskDialog">创建任务</el-button>
            <el-button @click="loadTasks">刷新</el-button>
          </div>
          
          <el-table 
            :data="taskList" 
            v-loading="taskLoading" 
            :height="'calc(100vh - 420px)'"
            style="width: 100%" 
            stripe 
            border
            :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
            :row-style="{ height: '50px' }"
            empty-text="暂无数据">
            <el-table-column prop="task_name" label="任务名称" width="200" show-overflow-tooltip>
              <template #default="scope">
                <div style="display: flex; align-items: center;">
                  <el-icon style="margin-right: 8px; color: #67c23a;"><Connection /></el-icon>
                  <span style="font-weight: 500;">{{ scope.row.task_name }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="last_execution_status" label="执行状态" width="120" align="center">
              <template #default="scope">
                <el-tag :type="getStatusTagType(scope.row.last_execution_status)" size="small">
                  {{ getStatusDisplayText(scope.row.last_execution_status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="source_location_arn" label="源位置" min-width="250" show-overflow-tooltip>
              <template #default="scope">
                <el-text class="uri-text" type="info">
                  <div style="display: flex; flex-direction: column;">
                    <span>{{ scope.row.source_location_display }}</span>
                    <span style="font-size: 12px; color: #909399;">{{ scope.row.source_location_arn }}</span>
                  </div>
                </el-text>
              </template>
            </el-table-column>
            <el-table-column prop="destination_location_arn" label="目标位置" min-width="250" show-overflow-tooltip>
              <template #default="scope">
                <el-text class="uri-text" type="info">
                  <div style="display: flex; flex-direction: column;">
                    <span>{{ scope.row.destination_location_display }}</span>
                    <span style="font-size: 12px; color: #909399;">{{ scope.row.destination_location_arn }}</span>
                  </div>
                </el-text>
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="创建时间" width="180" align="center">
              <template #default="scope">
                <span style="color: #909399;">{{ scope.row.created_at || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="240" align="center" fixed="right">
              <template #default="scope">
                <el-button 
                  size="small" 
                  type="success" 
                  @click="startTask(scope.row)" 
                  :loading="scope.row.starting"
                  :disabled="isTaskRunning(scope.row.last_execution_status)">
                  <el-icon><VideoPlay /></el-icon>
                  {{ getStartButtonText(scope.row.last_execution_status) }}
                </el-button>
                <el-button size="small" type="primary" link @click="viewTaskDetail(scope.row)">
                  <el-icon><View /></el-icon>
                  详情
                </el-button>
                <el-button size="small" type="info" link @click="viewExecutions(scope.row)">
                  <el-icon><List /></el-icon>
                  记录
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          
          <div class="pagination-container">
            <el-pagination
              @size-change="handleTaskSizeChange"
              @current-change="handleTaskCurrentChange"
              :current-page="taskPage"
              :page-sizes="[12, 24, 36, 50]"
              :page-size="taskSize"
              layout="total, sizes, prev, pager, next"
              :total="taskTotal"
              :background="true"
              :small="true"
              :hide-on-single-page="false">
            </el-pagination>
          </div>
        </div>
      </el-tab-pane>

      <!-- 执行记录 -->
      <el-tab-pane label="执行记录" name="executions">
        <div class="tab-content">
                  <div class="toolbar">
          <el-button @click="loadExecutions">刷新</el-button>
          <el-button type="primary" @click="syncAllRunningExecutionsAction" :loading="syncExecutionsLoading">
            同步所有运行状态
          </el-button>
        </div>
          
          <el-table 
            :data="executionList" 
            v-loading="executionLoading" 
            :height="'calc(100vh - 420px)'"
            style="width: 100%" 
            stripe 
            border
            :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
            :row-style="{ height: '50px' }"
            empty-text="暂无数据">
            <el-table-column prop="task_execution_arn" label="执行ARN" min-width="250" show-overflow-tooltip>
              <template #default="scope">
                <el-text class="uri-text" type="info">{{ scope.row.task_execution_arn }}</el-text>
              </template>
            </el-table-column>
            <el-table-column prop="execution_status" label="状态" width="120" align="center">
              <template #default="scope">
                <el-tag :type="getStatusTagType(scope.row.execution_status)" size="small">
                  {{ scope.row.execution_status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="files_transferred" label="传输文件数" width="140" align="center">
              <template #default="scope">
                <span style="font-weight: 500; color: #409eff;">{{ scope.row.files_transferred || 0 }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="bytes_transferred" label="传输字节数" width="150" align="center">
              <template #default="scope">
                <span style="font-weight: 500; color: #67c23a;">{{ formatBytes(scope.row.bytes_transferred) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="total_duration" label="耗时(秒)" width="120" align="center">
              <template #default="scope">
                <span style="color: #e6a23c;">{{ scope.row.total_duration || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="start_time" label="开始时间" width="180" align="center">
              <template #default="scope">
                <span style="color: #909399;">{{ scope.row.start_time || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="end_time" label="结束时间" width="180" align="center">
              <template #default="scope">
                <span style="color: #909399;">{{ scope.row.end_time || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="180" align="center" fixed="right">
              <template #default="scope">
                <el-button size="small" type="warning" @click="syncExecutionStatusAction(scope.row)" 
                  :loading="scope.row.syncing" v-if="isTaskRunning(scope.row.execution_status)">
                  <el-icon><Refresh /></el-icon>
                  同步
                </el-button>
                <el-button size="small" type="primary" link @click="viewExecutionDetail(scope.row)">
                  <el-icon><View /></el-icon>
                  详情
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          
          <div class="pagination-container">
            <el-pagination
              @size-change="handleExecutionSizeChange"
              @current-change="handleExecutionCurrentChange"
              :current-page="executionPage"
              :page-sizes="[12, 24, 36, 50]"
              :page-size="executionSize"
              layout="total, sizes, prev, pager, next"
              :total="executionTotal"
              :background="true"
              :small="true"
              :hide-on-single-page="false">
            </el-pagination>
          </div>
        </div>
      </el-tab-pane>

      <!-- 代理管理 -->
      <el-tab-pane label="代理管理" name="agents">
        <div class="tab-content">
          <div class="toolbar">
            <el-button type="primary" @click="handleSyncAgents" :loading="agentLoading">同步代理状态</el-button>
          </div>
          
          <el-table
            v-loading="agentLoading"
            :data="agentList"
            :height="'calc(100vh - 420px)'"
            style="width: 100%"
            border
            :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
            :row-style="{ height: '50px' }"
            :empty-text="emptyText"
          >
            <el-table-column prop="name" label="代理名称" min-width="150">
              <template #default="{ row }">
                <div style="display: flex; align-items: center;">
                  <el-icon style="margin-right: 8px; color: #409eff;"><Connection /></el-icon>
                  <span style="font-weight: 500;">{{ row.name }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="agent_arn" label="代理ARN" min-width="300" show-overflow-tooltip>
              <template #default="{ row }">
                <el-text class="uri-text" type="info">{{ row.agent_arn }}</el-text>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="100" align="center">
              <template #default="{ row }">
                <el-tag :type="getStatusType(row.status)">{{ row.status }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="endpoint_type" label="端点类型" width="120" align="center">
              <template #default="{ row }">
                <el-tag type="info" size="small">{{ row.endpoint_type }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="region" label="区域" width="120" align="center">
              <template #default="{ row }">
                <el-tag type="warning" size="small">{{ row.region }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="创建时间" width="180" align="center">
              <template #default="{ row }">
                <span style="color: #909399;">{{ row.created_at || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="240" fixed="right" align="center">
              <template #default="{ row }">
                <el-button
                  type="primary"
                  link
                  size="small"
                  @click="handleViewDetails(row)"
                >
                  <el-icon><View /></el-icon>
                  详情
                </el-button>
                <el-button
                  type="success"
                  link
                  size="small"
                  @click="handleViewAgentLocations(row)"
                >
                  <el-icon><Connection /></el-icon>
                  位置
                </el-button>
                <el-button
                  type="warning"
                  link
                  size="small"
                  @click="handleRefreshStatus(row)"
                >
                  <el-icon><Refresh /></el-icon>
                  刷新
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          
          <div class="pagination-container" v-if="agentTotal > 0">
            <el-pagination
              v-model:current-page="agentQuery.page"
              v-model:page-size="agentQuery.size"
              :page-sizes="[12, 24, 36, 50]"
              :total="agentTotal"
              layout="total, sizes, prev, pager, next"
              @size-change="handleAgentSizeChange"
              @current-change="handleAgentPageChange"
              :background="true"
              :small="true"
            />
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>
    </div>
    <!-- 创建位置对话框 -->
    <el-dialog
      v-model="createLocationVisible"
      title="创建DataSync位置"
      width="600px"
      :close-on-click-modal="false"
      @closed="$refs.locationFormRef?.resetFields()">
      <el-form :model="locationForm" :rules="locationRules" ref="locationFormRef" label-width="120px">
        <el-form-item label="位置类型" prop="location_type">
          <el-select v-model="locationForm.location_type" placeholder="选择位置类型">
            <el-option label="S3" value="S3"></el-option>
            <el-option label="EFS" value="EFS" disabled></el-option>
            <el-option label="NFS" value="NFS" disabled></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="显示名称" prop="display_name">
          <el-input v-model="locationForm.display_name" placeholder="输入位置显示名称"></el-input>
        </el-form-item>
        <el-form-item label="S3存储桶" prop="bucket_name" v-if="locationForm.location_type === 'S3'">
          <el-input v-model="locationForm.bucket_name" placeholder="输入S3存储桶名称"></el-input>
        </el-form-item>
        <el-form-item label="子目录" v-if="locationForm.location_type === 'S3'">
          <el-input v-model="locationForm.subdirectory" placeholder="输入子目录，默认为/"></el-input>
        </el-form-item>
        <el-form-item label="存储类别" v-if="locationForm.location_type === 'S3'">
          <el-select v-model="locationForm.storage_class">
            <el-option label="STANDARD" value="STANDARD"></el-option>
            <el-option label="STANDARD_IA" value="STANDARD_IA"></el-option>
            <el-option label="GLACIER" value="GLACIER"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="描述">
          <el-input type="textarea" v-model="locationForm.description" placeholder="输入位置描述"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createLocationVisible = false">取消</el-button>
        <el-button type="primary" @click="createLocation" :loading="createLocationLoading">创建</el-button>
      </template>
    </el-dialog>

    <!-- 位置详情对话框 -->
    <el-dialog
      v-model="locationDetailVisible"
      title="位置详情"
      width="800px"
      :close-on-click-modal="false">
      <div v-if="selectedLocation" class="detail-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="位置ARN" :span="2">
            <el-text type="info" class="uri-text">{{ selectedLocation.location_arn }}</el-text>
          </el-descriptions-item>
          <el-descriptions-item label="位置名称">
            <span style="font-weight: 500;">{{ selectedLocation.display_name }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="本地别名">
            <span style="font-weight: 500;">{{ selectedLocation.local_alias || '未设置' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="位置类型">
            <el-tag :type="getLocationTypeColor(selectedLocation.location_type)" size="small">
              {{ selectedLocation.location_type }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="区域">
            <el-tag type="info" size="small" plain>{{ selectedLocation.region }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="位置URI" :span="2">
            <el-text type="info" class="uri-text">{{ selectedLocation.location_uri }}</el-text>
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="selectedLocation.status === 1 ? 'success' : 'danger'" size="small">
              {{ selectedLocation.status === 1 ? '正常' : '禁用' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">
            <span style="color: #909399;">{{ selectedLocation.created_at || '未知' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="描述" :span="2">
            <span>{{ selectedLocation.description || '无描述' }}</span>
          </el-descriptions-item>

          <!-- 配置信息 -->
          <el-descriptions-item label="访问密钥" :span="2" v-if="selectedLocation.config?.AccessKey">
            <el-text type="info" class="uri-text">{{ selectedLocation.config.AccessKey }}</el-text>
          </el-descriptions-item>
          <el-descriptions-item label="服务器端口" v-if="selectedLocation.config?.ServerPort">
            <span>{{ selectedLocation.config.ServerPort }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="服务器协议" v-if="selectedLocation.config?.ServerProtocol">
            <el-tag size="small" type="warning">{{ selectedLocation.config.ServerProtocol }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="绑定的代理" :span="2" v-if="locationAgentsList.length > 0 || locationAgentsLoading">
            <div v-loading="locationAgentsLoading" class="agent-section">
              <div v-if="locationAgentsList.length === 0 && !locationAgentsLoading" class="no-agents">
                <el-text type="info">该位置未绑定任何代理</el-text>
              </div>
              <div v-else class="agents-list">
                <div v-for="(agent, index) in locationAgentsList" :key="index" class="agent-item">
                  <div class="agent-card">
                    <div class="agent-header">
                      <div class="agent-name">
                        <el-icon><Connection /></el-icon>
                        <span class="name">{{ agent.name }}</span>
                        <el-tag 
                          :type="agent.agent_status === 'ONLINE' ? 'success' : agent.agent_status === 'OFFLINE' ? 'danger' : 'warning'" 
                          size="small">
                          {{ agent.agent_status }}
                        </el-tag>
                        <el-tag v-if="agent.is_missing" type="danger" size="small">数据缺失</el-tag>
                      </div>
                      <div class="agent-arn">
                        <el-text type="info" class="uri-text">{{ agent.agent_arn }}</el-text>
                      </div>
                    </div>
                    <div class="agent-details" v-if="!agent.is_missing">
                      <div class="detail-row">
                        <span class="label">端点类型:</span>
                        <el-tag size="small" type="info">{{ agent.endpoint_type }}</el-tag>
                      </div>
                      <div class="detail-row" v-if="agent.region">
                        <span class="label">区域:</span>
                        <el-tag size="small" type="warning">{{ agent.region }}</el-tag>
                      </div>
                      <div class="detail-row" v-if="agent.created_at">
                        <span class="label">创建时间:</span>
                        <span class="value">{{ agent.created_at }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <template #footer>
        <el-button @click="locationDetailVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 任务详情对话框 -->
    <el-dialog
      v-model="taskDetailVisible"
      title="任务详情"
      width="900px"
      :close-on-click-modal="false">
      <div v-if="selectedTask" class="detail-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="任务ARN" :span="2">
            <el-text type="info" class="uri-text">{{ selectedTask.task_arn }}</el-text>
          </el-descriptions-item>
          <el-descriptions-item label="任务名称">
            <span style="font-weight: 500;">{{ selectedTask.task_name }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="执行状态">
            <el-tag :type="getStatusTagType(selectedTask.last_execution_status)" size="small">
              {{ getStatusDisplayText(selectedTask.last_execution_status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="源位置ARN" :span="2">
            <el-text type="info" class="uri-text">{{ selectedTask.source_location_arn }}</el-text>
          </el-descriptions-item>
          <el-descriptions-item label="目标位置ARN" :span="2">
            <el-text type="info" class="uri-text">{{ selectedTask.destination_location_arn }}</el-text>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间" :span="2">
            <span style="color: #909399;">{{ selectedTask.created_at || '未知' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="最后执行时间" :span="2">
            <span style="color: #909399;">{{ selectedTask.last_execution_time || '未执行' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="任务描述" :span="2">
            <span>{{ selectedTask.description || '无描述' }}</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <template #footer>
        <div style="display: flex; justify-content: space-between;">
          <el-button 
            type="success" 
            @click="startTaskFromDetail" 
            :loading="selectedTask?.starting"
            :disabled="isTaskRunning(selectedTask?.last_execution_status)">
            <el-icon><VideoPlay /></el-icon>
            {{ getStartButtonText(selectedTask?.last_execution_status) }}
          </el-button>
          <el-button @click="taskDetailVisible = false">关闭</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 执行记录详情对话框 -->
    <el-dialog
      v-model="executionDetailVisible"
      title="执行记录详情"
      width="800px"
      :close-on-click-modal="false">
      <div v-if="selectedExecution" class="detail-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="执行ARN" :span="2">
            <el-text type="info" class="uri-text">{{ selectedExecution.task_execution_arn }}</el-text>
          </el-descriptions-item>
          <el-descriptions-item label="执行状态">
            <el-tag :type="getStatusTagType(selectedExecution.execution_status)" size="small">
              {{ selectedExecution.execution_status }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="总耗时">
            <span style="color: #e6a23c; font-weight: 500;">{{ selectedExecution.total_duration || 0 }} 秒</span>
          </el-descriptions-item>
          <el-descriptions-item label="传输文件数">
            <span style="font-weight: 500; color: #409eff;">{{ selectedExecution.files_transferred || 0 }} 个</span>
          </el-descriptions-item>
          <el-descriptions-item label="传输字节数">
            <span style="font-weight: 500; color: #67c23a;">{{ formatBytes(selectedExecution.bytes_transferred) }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="开始时间" :span="2">
            <span style="color: #909399;">{{ selectedExecution.start_time || '未知' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="结束时间" :span="2">
            <span style="color: #909399;">{{ selectedExecution.end_time || '未知' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="验证状态" v-if="selectedExecution.verification_status">
            <el-tag :type="selectedExecution.verification_status === 'SUCCESS' ? 'success' : 'warning'" size="small">
              {{ selectedExecution.verification_status }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="错误信息" v-if="selectedExecution.error_message" :span="2">
            <el-text type="danger">{{ selectedExecution.error_message }}</el-text>
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <template #footer>
        <el-button @click="executionDetailVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 创建任务对话框 -->
    <el-dialog
      v-model="createTaskVisible"
      title="创建DataSync任务"
      width="700px"
      :close-on-click-modal="false"
      @closed="$refs.taskFormRef?.resetFields()">
      <el-form :model="taskForm" :rules="taskRules" ref="taskFormRef" label-width="120px">
        <el-form-item label="任务名称" prop="task_name">
          <el-input v-model="taskForm.task_name" placeholder="输入任务名称"></el-input>
        </el-form-item>
        <el-form-item label="源位置" prop="source_location_arn">
          <el-select v-model="taskForm.source_location_arn" placeholder="选择源位置" filterable>
            <el-option
              v-for="location in locationList"
              :key="location.location_arn"
              :label="location.local_alias || location.display_name"
              :value="location.location_arn">
              <div style="display: flex; flex-direction: column;">
                <span>{{ location.local_alias || location.display_name }}</span>
                <span style="font-size: 12px; color: #909399;">{{ location.location_arn }}</span>
              </div>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="目标位置" prop="destination_location_arn">
          <el-select v-model="taskForm.destination_location_arn" placeholder="选择目标位置" filterable>
            <el-option
              v-for="location in locationList"
              :key="location.location_arn"
              :label="location.local_alias || location.display_name"
              :value="location.location_arn">
              <div style="display: flex; flex-direction: column;">
                <span>{{ location.local_alias || location.display_name }}</span>
                <span style="font-size: 12px; color: #909399;">{{ location.location_arn }}</span>
              </div>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="任务描述">
          <el-input type="textarea" v-model="taskForm.description" placeholder="输入任务描述"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createTaskVisible = false">取消</el-button>
        <el-button type="primary" @click="createTask" :loading="createTaskLoading">创建</el-button>
      </template>
    </el-dialog>

    <!-- 代理详情对话框 -->
    <el-dialog
      v-model="agentDialogVisible"
      title="代理详情"
      width="600px"
      :close-on-click-modal="false"
    >
      <div v-if="currentAgent" class="detail-content">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="代理名称">
            <span style="font-weight: 500;">{{ currentAgent.name }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="代理ARN">
            <el-text type="info" class="uri-text">{{ currentAgent.agent_arn }}</el-text>
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusType(currentAgent.status)">
              {{ currentAgent.status }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="端点类型">
            <el-tag type="info" size="small">{{ currentAgent.endpoint_type }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="区域">
            <el-tag type="warning" size="small">{{ currentAgent.region }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="私有链接端点" v-if="currentAgent.private_link_endpoint">
            <el-text type="info" class="uri-text">{{ currentAgent.private_link_endpoint }}</el-text>
          </el-descriptions-item>
          <el-descriptions-item label="VPC端点ID" v-if="currentAgent.vpc_endpoint_id">
            <el-text type="info">{{ currentAgent.vpc_endpoint_id }}</el-text>
          </el-descriptions-item>
          <el-descriptions-item label="子网ARN" v-if="currentAgent.subnet_arns && currentAgent.subnet_arns.length">
            <div class="arn-list">
              <div v-for="(arn, index) in currentAgent.subnet_arns" :key="index" class="arn-item">
                <el-text type="info" class="uri-text">{{ arn }}</el-text>
              </div>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="安全组ARN" v-if="currentAgent.security_group_arns && currentAgent.security_group_arns.length">
            <div class="arn-list">
              <div v-for="(arn, index) in currentAgent.security_group_arns" :key="index" class="arn-item">
                <el-text type="info" class="uri-text">{{ arn }}</el-text>
              </div>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">
            <span style="color: #909399;">{{ currentAgent.created_at }}</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <template #footer>
        <el-button @click="agentDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
    


    <!-- 代理位置对话框 -->
    <el-dialog
      v-model="agentLocationsDialogVisible"
      title="代理绑定的位置"
      width="1000px"
      :close-on-click-modal="false"
    >
      <div v-if="currentAgentForLocations" style="margin-bottom: 16px;">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="代理名称">
            <span style="font-weight: 500;">{{ currentAgentForLocations.name }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusType(currentAgentForLocations.status)">
              {{ currentAgentForLocations.status }}
            </el-tag>
          </el-descriptions-item>
        </el-descriptions>
      </div>
      
      <div style="margin-bottom: 16px;">
        <el-button type="primary" @click="handleSyncAgentLocations" :loading="agentLocationsLoading">
          <el-icon><Refresh /></el-icon>
          同步位置信息
        </el-button>
      </div>
      
      <el-table
        v-loading="agentLocationsLoading"
        :data="paginatedAgentLocations"
        style="width: 100%"
        border
        :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
        :empty-text="agentLocationsList.length === 0 ? '该代理未绑定任何位置' : '加载中...'"
      >
        <el-table-column prop="display_name" label="位置名称" min-width="70">
          <template #default="{ row }">
            <div style="display: flex; align-items: center;">
              <el-icon style="margin-right: 8px; color: #409eff;"><Document /></el-icon>
              <span style="font-weight: 500;">{{ row.local_alias || row.display_name }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="location_type" label="位置类型" width="150" align="center">
          <template #default="{ row }">
            <el-tag :color="getLocationTypeColor(row.location_type)" style="color: #409eff;">
              {{ row.location_type }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="location_uri" label="位置URI" min-width="200" show-overflow-tooltip>
          <template #default="{ row }">
            <el-text class="uri-text" type="info">{{ row.location_uri }}</el-text>
          </template>
        </el-table-column>
        <el-table-column prop="region" label="区域" width="120" align="center">
          <template #default="{ row }">
            <el-tag type="warning" size="small">{{ row.region }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '正常' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="180" align="center">
          <template #default="{ row }">
            <span style="color: #909399;">{{ row.created_at || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center">
          <template #default="{ row }">
            <el-button
              type="primary"
              link
              size="small"
              @click="viewLocationDetail(row)"
            >
              <el-icon><View /></el-icon>
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页组件 -->
      <div class="pagination-container" v-if="agentLocationsTotal > 0">
        <el-pagination
          v-model:current-page="agentLocationsPage"
          v-model:page-size="agentLocationsSize"
          :page-sizes="[5, 10, 20, 50]"
          :total="agentLocationsTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleAgentLocationsSizeChange"
          @current-change="handleAgentLocationsPageChange"
          :background="true"
          :small="true"
        />
      </div>
      
      <template #footer>
        <el-button @click="agentLocationsDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted, nextTick, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { User, Document, Connection, VideoPlay, View, List, Refresh, Edit } from '@element-plus/icons-vue'
import { 
  getDataSyncLocations, 
  createDataSyncLocation, 
  getDataSyncTasks, 
  createDataSyncTask, 
  getDataSyncExecutions, 
  executeDataSyncTask, 
  syncDataSyncFromAws,
  syncExecutionStatus,
  syncAllRunningExecutions,
  updateDataSyncLocationAlias,
  listDataSyncAgents,
  syncDataSyncAgents,
  updateAgentStatus,
  getAgentLocations,
  getLocationAgents,
  syncAgentLocations
} from '@/api/api'

export default {
  name: 'DataSyncManage',
  components: {
    User,
    Document,
    Connection,
    VideoPlay,
    View,
    List,
    Refresh,
    Edit
  },
  setup() {
    const activeTab = ref('locations')
    const selectedPayerId = ref('130712356846')
    const selectedPayerName = ref('vstecs-pay12-global')
    
    // 位置相关
    const locationList = ref([])
    const locationLoading = ref(false)
    const locationPage = ref(1)
    const locationSize = ref(12)
    const locationTotal = ref(0)
    const locationSort = ref({
      prop: '',
      order: ''
    })
    
    // 任务相关
    const taskList = ref([])
    const taskLoading = ref(false)
    const taskPage = ref(1)
    const taskSize = ref(12)
    const taskTotal = ref(0)
    
    // 执行记录相关
    const executionList = ref([])
    const executionLoading = ref(false)
    const executionPage = ref(1)
    const executionSize = ref(12)
    const executionTotal = ref(0)
    
    // 同步
    const syncLoading = ref(false)
    const syncExecutionsLoading = ref(false)
    
    // 自动刷新定时器
    const autoRefreshTimer = ref(null)
    
    // 创建位置
    const createLocationVisible = ref(false)
    const createLocationLoading = ref(false)
    const locationForm = reactive({
      location_type: 'S3',
      display_name: '',
      bucket_name: '',
      subdirectory: '/',
      storage_class: 'STANDARD',
      description: ''
    })
    const locationRules = {
      location_type: [{ required: true, message: '请选择位置类型', trigger: 'change' }],
      display_name: [{ required: true, message: '请输入显示名称', trigger: 'blur' }],
      bucket_name: [{ required: true, message: '请输入S3存储桶名称', trigger: 'blur' }]
    }
    
    // 创建任务
    const createTaskVisible = ref(false)
    const createTaskLoading = ref(false)
    
    // 详情对话框
    const locationDetailVisible = ref(false)
    const taskDetailVisible = ref(false)
    const executionDetailVisible = ref(false)
    const selectedLocation = ref(null)
    const selectedTask = ref(null)
    const selectedExecution = ref(null)
    const taskForm = reactive({
      task_name: '',
      source_location_arn: '',
      destination_location_arn: '',
      description: ''
    })
    const taskRules = {
      task_name: [{ required: true, message: '请输入任务名称', trigger: 'blur' }],
      source_location_arn: [{ required: true, message: '请选择源位置', trigger: 'change' }],
      destination_location_arn: [{ required: true, message: '请选择目标位置', trigger: 'change' }]
    }

    // Agent相关
    const agentList = ref([])
    const agentTotal = ref(0)
    const agentLoading = ref(false)
    const agentQuery = reactive({
      page: 1,
      size: 12,
      sort_by: '',
      sort_order: 'desc'
    })
    const currentAgent = ref(null)
    const agentDialogVisible = ref(false)

    // 代理位置相关
    const agentLocationsDialogVisible = ref(false)
    const agentLocationsLoading = ref(false)
    const agentLocationsList = ref([])
    const agentLocationsTotal = ref(0)
    const agentLocationsPage = ref(1)
    const agentLocationsSize = ref(10)
    const currentAgentForLocations = ref(null)

    // 位置代理相关
    const locationAgentsList = ref([])
    const locationAgentsLoading = ref(false)

    // Payer变更 (由于固定使用vstecs-pay12-global，这个方法主要用于初始化)
    const onPayerChange = () => {
      if (selectedPayerId.value) {
        loadLocations()
        loadTasks()
        loadExecutions()
      }
    }

    // 加载位置列表
    const loadLocations = async () => {
      if (!selectedPayerId.value) {
        console.warn('selectedPayerId为空，无法加载位置列表')
        return
      }
      
      locationLoading.value = true
      try {
        const params = {
          payer_id: selectedPayerId.value,
          page: locationPage.value,
          size: locationSize.value
        }
        
        // 只在有排序参数时才添加排序字段
        if (locationSort.value.prop && locationSort.value.order) {
          params.sort_by = locationSort.value.prop
          params.sort_order = locationSort.value.order === 'ascending' ? 'asc' : 'desc'
        }
        
        console.log('正在加载位置列表，参数:', params)
        const response = await getDataSyncLocations(params)
        console.log('位置列表响应:', response)
        
        if (response.success) {
          locationList.value = response.data.list || []
          locationTotal.value = response.data.total || response.data.list?.length || 0
          console.log(`成功加载${locationList.value.length}个位置，总数:${locationTotal.value}`)
        } else {
          console.error('加载位置列表失败:', response.message)
          ElMessage.error(response.message || '加载位置列表失败')
        }
      } catch (error) {
        console.error('加载位置列表异常:', error)
        ElMessage.error('加载位置列表失败: ' + error.message)
      } finally {
        locationLoading.value = false
      }
    }

    // 加载任务列表
    const loadTasks = async () => {
      if (!selectedPayerId.value) return
      
      taskLoading.value = true
      try {
        const response = await getDataSyncTasks({
          payer_id: selectedPayerId.value,
          page: taskPage.value,
          size: taskSize.value
        })
        if (response.success) {
          taskList.value = response.data.list || []
          taskTotal.value = response.data.total || response.data.list?.length || 0
        } else {
          ElMessage.error(response.message || '加载任务列表失败')
        }
      } catch (error) {
        console.error('加载任务列表失败:', error)
        ElMessage.error('加载任务列表失败')
      } finally {
        taskLoading.value = false
      }
    }

    // 加载执行记录
    const loadExecutions = async () => {
      if (!selectedPayerId.value) return
      
      executionLoading.value = true
      try {
        const response = await getDataSyncExecutions({
          payer_id: selectedPayerId.value,
          page: executionPage.value,
          size: executionSize.value
        })

        if (response.success) {
          executionList.value = response.data.list || []
          executionTotal.value = response.data.total || response.data.list?.length || 0
        } else {
          ElMessage.error(response.message || '加载执行记录失败')
        }
      } catch (error) {
        ElMessage.error('加载执行记录失败')
      } finally {
        executionLoading.value = false
      }
    }

    // 从AWS同步
    const syncFromAws = async () => {
      try {
        syncLoading.value = true
        const response = await syncDataSyncFromAws({
          payer_id: selectedPayerId.value
        })
        if (response.success) {
          const data = response.data || {}
          const syncedLocations = data.synced_locations || 0
          const syncedTasks = data.synced_tasks || 0
          const updatedTasks = data.updated_tasks || 0
          const syncedExecutions = data.synced_executions || 0
          const syncedAgents = data.synced_agents || 0
          
          const syncMessage = `同步成功：位置 ${syncedLocations} 个，任务 ${syncedTasks} 个${updatedTasks > 0 ? `（更新 ${updatedTasks} 个）` : ''}，执行记录 ${syncedExecutions} 个，代理 ${syncedAgents} 个`
          ElMessage.success(syncMessage)
          
          // 重新加载数据
          loadLocations()
          loadTasks()
          fetchAgentList()
        } else {
          ElMessage.error(response.message || '同步失败')
        }
      } catch (error) {
        console.error('从AWS同步失败:', error)
        ElMessage.error('同步失败')
      } finally {
        syncLoading.value = false
      }
    }

    // 显示创建位置对话框
    const showCreateLocationDialog = () => {
      createLocationVisible.value = true
    }

    // 创建位置
    const createLocation = async () => {
      createLocationLoading.value = true
      try {
        const data = {
          payer_id: selectedPayerId.value,
          ...locationForm
        }
        const response = await createDataSyncLocation(data)
        if (response.success) {
          ElMessage.success('位置创建成功')
          createLocationVisible.value = false
          loadLocations()
          // 重置表单
          Object.assign(locationForm, {
            location_type: 'S3',
            display_name: '',
            bucket_name: '',
            subdirectory: '/',
            storage_class: 'STANDARD',
            description: ''
          })
        } else {
          ElMessage.error(response.message || '创建失败')
        }
      } catch (error) {
        console.error('创建位置失败:', error)
        ElMessage.error('创建位置失败')
      } finally {
        createLocationLoading.value = false
      }
    }

    // 显示创建任务对话框
    const showCreateTaskDialog = () => {
      if (locationList.value.length === 0) {
        ElMessage.warning('请先创建位置')
        return
      }
      createTaskVisible.value = true
    }

    // 创建任务
    const createTask = async () => {
      createTaskLoading.value = true
      try {
        const data = {
          payer_id: selectedPayerId.value,
          ...taskForm
        }
        const response = await createDataSyncTask(data)
        if (response.success) {
          ElMessage.success('任务创建成功')
          createTaskVisible.value = false
          loadTasks()
          // 重置表单
          Object.assign(taskForm, {
            task_name: '',
            source_location_arn: '',
            destination_location_arn: '',
            description: ''
          })
        } else {
          ElMessage.error(response.message || '创建失败')
        }
      } catch (error) {
        console.error('创建任务失败:', error)
        ElMessage.error('创建任务失败')
      } finally {
        createTaskLoading.value = false
      }
    }

    // 启动任务
    const startTask = async (task) => {
      if (isTaskRunning(task.last_execution_status)) {
        ElMessage.warning(`任务正在${getStatusDisplayText(task.last_execution_status)}，请等待完成后再试`)
        return
      }

      try {
        task.starting = true
        const response = await executeDataSyncTask({
          payer_id: selectedPayerId.value,
          task_arn: task.task_arn
        })
        if (response.success) {
          ElMessage.success('任务启动成功')
          loadTasks()
          loadExecutions()
        } else {
          ElMessage.error(response.message || '启动失败')
        }
      } catch (error) {
        console.error('启动任务失败:', error)
        ElMessage.error('启动任务失败')
      } finally {
        task.starting = false
      }
    }

    // 工具函数
    const getStatusTagType = (status) => {
      const statusMap = {
        'SUCCESS': 'success',
        'QUEUED': 'info',
        'LAUNCHING': 'warning',
        'PREPARING': 'warning',
        'TRANSFERRING': 'primary',
        'VERIFYING': 'warning',
        'ERROR': 'danger'
      }
      return statusMap[status] || 'info'
    }

    const getStatusDisplayText = (status) => {
      const statusMap = {
        'SUCCESS': '成功',
        'QUEUED': '排队中',
        'LAUNCHING': '启动中',
        'PREPARING': '准备中',
        'TRANSFERRING': '传输中',
        'VERIFYING': '校验中',
        'ERROR': '错误'
      }
      return statusMap[status] || '未执行'
    }

    const isTaskRunning = (status) => {
      const runningStates = ['QUEUED', 'LAUNCHING', 'PREPARING', 'TRANSFERRING', 'VERIFYING']
      return runningStates.includes(status)
    }

    const getStartButtonText = (status) => {
      return isTaskRunning(status) ? '运行中' : '启动'
    }

    const getLocationTypeColor = (type) => {
      const typeMap = {
        'S3': '',  // 使用默认类型
        'EFS': 'success',
        'NFS': 'warning',
        'SMB': 'info',
        'FSX': 'danger',
        'OBJECT-STORAGE': 'warning'
      }
      return typeMap[type] || ''  // 如果找不到映射，返回空字符串使用默认类型
    }

    const formatBytes = (bytes) => {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    // 分页处理
    const handleLocationSizeChange = (size) => {
      locationSize.value = size
      loadLocations()
    }

    const handleLocationCurrentChange = (page) => {
      locationPage.value = page
      loadLocations()
    }

    const handleTaskSizeChange = (size) => {
      taskSize.value = size
      loadTasks()
    }

    const handleTaskCurrentChange = (page) => {
      taskPage.value = page
      loadTasks()
    }

    const handleExecutionSizeChange = (size) => {
      executionSize.value = size
      loadExecutions()
    }

    const handleExecutionCurrentChange = (page) => {
      executionPage.value = page
      loadExecutions()
    }

    // 查看详情方法
    const viewLocationDetail = async (location) => {
      selectedLocation.value = location
      locationDetailVisible.value = true
      
      // 加载位置绑定的代理信息
      await loadLocationAgents(location.location_arn)
    }

    const viewTaskDetail = (task) => {
      selectedTask.value = task
      taskDetailVisible.value = true
    }

    const viewExecutionDetail = (execution) => {
      selectedExecution.value = execution
      executionDetailVisible.value = true
    }

    // 从详情对话框启动任务
    const startTaskFromDetail = async () => {
      if (selectedTask.value) {
        await startTask(selectedTask.value)
        // 如果启动成功，更新详情对话框中的任务信息
        if (selectedTask.value.last_execution_status) {
          taskDetailVisible.value = false
          loadTasks() // 重新加载任务列表
        }
      }
    }

    const viewExecutions = (task) => {
      activeTab.value = 'executions'
    }

    // 启动自动刷新
    const startAutoRefresh = () => {
      if (autoRefreshTimer.value) {
        clearInterval(autoRefreshTimer.value)
      }
      // 每30秒刷新一次任务状态和代理状态
      autoRefreshTimer.value = setInterval(() => {
        if (activeTab.value === 'tasks') {
          loadTasks()
        } else if (activeTab.value === 'executions') {
          loadExecutions()
        } else if (activeTab.value === 'agents') {
          fetchAgentList()  // 添加代理列表的自动刷新
        }
      }, 30000)
    }

    // 停止自动刷新
    const stopAutoRefresh = () => {
      if (autoRefreshTimer.value) {
        clearInterval(autoRefreshTimer.value)
        autoRefreshTimer.value = null
      }
    }

    onMounted(() => {
      // 直接加载数据，因为已经固定使用vstecs-pay12-global账户
      onPayerChange()
      // 启动自动刷新
      // startAutoRefresh()
      // 先获取代理列表
      fetchAgentList()
    })

    // 同步单个执行状态
    const syncExecutionStatusAction = async (execution) => {
      try {
        execution.syncing = true
        const response = await syncExecutionStatus({
          payer_id: selectedPayerId.value,
          execution_arn: execution.task_execution_arn
        })
        if (response.success) {
          ElMessage.success('执行状态同步成功')
          loadExecutions()
          loadTasks() // 也刷新任务列表以更新状态
        } else {
          ElMessage.error(response.message || '同步失败')
        }
      } catch (error) {
        console.error('同步执行状态失败:', error)
        ElMessage.error('同步执行状态失败')
      } finally {
        execution.syncing = false
      }
    }

    // 同步所有正在运行的执行状态
    const syncAllRunningExecutionsAction = async () => {
      try {
        syncExecutionsLoading.value = true
        const response = await syncAllRunningExecutions({
          payer_id: selectedPayerId.value
        })
        if (response.success) {
          ElMessage.success(`同步完成，更新了${response.data.updated_count}个执行记录`)
          loadExecutions()
          loadTasks() // 也刷新任务列表以更新状态
        } else {
          ElMessage.error(response.message || '批量同步失败')
        }
      } catch (error) {
        console.error('批量同步失败:', error)
        ElMessage.error('批量同步失败')
      } finally {
        syncExecutionsLoading.value = false
      }
    }

    // 更新本地别名
    const updateLocalAlias = async (location) => {
      try {
        const response = await updateDataSyncLocationAlias({
          payer_id: selectedPayerId.value,
          location_arn: location.location_arn,
          local_alias: location.local_alias
        })
        if (response.success) {
          ElMessage.success('别名更新成功')
        } else {
          ElMessage.error(response.message || '更新失败')
          // 如果更新失败，重新加载数据
          loadLocations()
        }
      } catch (error) {
        console.error('更新别名失败:', error)
        ElMessage.error('更新别名失败')
        loadLocations()
      }
    }

    // 开始编辑别名
    const startEdit = (row) => {
      row.isEditing = true
      // 等待 DOM 更新后聚焦输入框
      nextTick(() => {
        const input = document.querySelector('.alias-cell .el-input__inner')
        if (input) {
          input.focus()
        }
      })
    }

    // 完成编辑
    const finishEdit = async (row) => {
      row.isEditing = false
      await updateLocalAlias(row)
    }

    // 排序处理
    const handleSortChange = ({ prop, order }) => {
      locationSort.value = { prop, order }
      locationPage.value = 1  // 切换排序时重置到第一页
      loadLocations()
    }

    // Agent相关方法
    const fetchAgentList = async () => {
      if (!selectedPayerId.value) return
      
      agentLoading.value = true
      try {
        const response = await listDataSyncAgents({
          ...agentQuery,  // 注意这里不需要 .value，因为已经是 reactive 对象
          payer_id: selectedPayerId.value
        })
        if (response.success) {
          agentList.value = response.data.list || []
          agentTotal.value = response.data.total || 0
        } else {
          ElMessage.error(response.message || '获取代理列表失败')
        }
      } catch (error) {
        console.error('获取代理列表失败:', error)
        ElMessage.error('获取代理列表失败，请检查网络连接')
      } finally {
        agentLoading.value = false
      }
    }
    
    const handleSyncAgents = async () => {
      if (!selectedPayerId.value) {
        ElMessage.warning('请先选择账号')
        return
      }
      
      try {
        ElMessage.info('正在从AWS同步代理信息...')
        const response = await syncDataSyncAgents({
          payer_id: selectedPayerId.value
        })
        if (response.success) {
          ElMessage.success(`同步成功，更新了 ${response.data.updated_count} 个代理`)
          // 短暂延迟后再获取最新列表，确保后端数据已更新
          setTimeout(() => {
            fetchAgentList()
          }, 1000)
        } else {
          ElMessage.error(response.message || '同步代理状态失败')
        }
      } catch (error) {
        console.error('同步代理状态失败:', error)
        ElMessage.error('同步代理状态失败，请检查网络连接和AWS配置')
      }
    }
    

    
    const handleViewDetails = (row) => {
      currentAgent.value = row
      agentDialogVisible.value = true
    }
    
    const handleRefreshStatus = async (row) => {
      if (!selectedPayerId.value) {
        ElMessage.warning('请先选择账号')
        return
      }
      
      if (!row.agent_arn) {
        ElMessage.warning('代理ARN不能为空')
        return
      }
      
      try {
        const response = await updateAgentStatus({
          payer_id: selectedPayerId.value,
          agent_arn: row.agent_arn
        })
        if (response.success) {
          ElMessage.success('状态更新成功')
          fetchAgentList()
        } else {
          ElMessage.error(response.message || '更新代理状态失败')
        }
      } catch (error) {
        console.error('更新代理状态失败:', error)
        ElMessage.error('更新代理状态失败')
      }
    }
    
    const getStatusType = (status) => {
      const statusMap = {
        'ONLINE': 'success',
        'OFFLINE': 'danger',
        'PENDING': 'warning'
      }
      return statusMap[status] || 'info'
    }
    
    const handleAgentSizeChange = (size) => {
      agentQuery.size = size
      fetchAgentList()
    }
    
    const handleAgentPageChange = (page) => {
      agentQuery.page = page
      fetchAgentList()
    }

    // 添加代理表格的空数据提示
    const emptyText = computed(() => {
      return agentLoading.value ? '加载中...' : '暂无代理数据，请点击"同步代理状态"从AWS获取最新数据'
    })

    // 代理位置分页计算属性
    const paginatedAgentLocations = computed(() => {
      const start = (agentLocationsPage.value - 1) * agentLocationsSize.value
      const end = start + agentLocationsSize.value
      return agentLocationsList.value.slice(start, end)
    })

    // 代理位置相关方法
    const handleViewAgentLocations = async (agent) => {
      if (!selectedPayerId.value) {
        ElMessage.warning('请先选择账号')
        return
      }
      
      currentAgentForLocations.value = agent
      agentLocationsDialogVisible.value = true
      
      // 获取代理绑定的位置
      await loadAgentLocations(agent.agent_arn)
    }
    
    const loadAgentLocations = async (agentArn) => {
      agentLocationsLoading.value = true
      try {
        const response = await getAgentLocations({
          payer_id: selectedPayerId.value,
          agent_arn: agentArn
        })
        if (response.success) {
          agentLocationsList.value = response.data.locations || []
          agentLocationsTotal.value = response.data.total || 0
          agentLocationsPage.value = 1 // 重置到第一页
          // 只在找到位置时显示成功消息，避免频繁弹出告警
          if (agentLocationsTotal.value > 0) {
            ElMessage.success(`找到 ${agentLocationsTotal.value} 个绑定的位置`)
          }
          // 未绑定位置时不显示消息，用户可以通过表格看到为空
        } else {
          ElMessage.error(response.message || '获取代理位置失败')
          agentLocationsList.value = []
          agentLocationsTotal.value = 0
        }
      } catch (error) {
        console.error('获取代理位置失败:', error)
        ElMessage.error('获取代理位置失败，请检查网络连接')
        agentLocationsList.value = []
        agentLocationsTotal.value = 0
      } finally {
        agentLocationsLoading.value = false
      }
    }
    
    const handleSyncAgentLocations = async () => {
      if (!currentAgentForLocations.value) return
      
      agentLocationsLoading.value = true
      try {
        ElMessage.info('正在从AWS同步代理位置绑定信息...')
        const response = await syncAgentLocations({
          payer_id: selectedPayerId.value,
          agent_arn: currentAgentForLocations.value.agent_arn
        })
        if (response.success) {
          ElMessage.success(`同步成功，更新了 ${response.data.updated_count} 个位置`)
          // 重新加载代理位置列表
          await loadAgentLocations(currentAgentForLocations.value.agent_arn)
        } else {
          ElMessage.error(response.message || '同步代理位置失败')
        }
      } catch (error) {
        console.error('同步代理位置失败:', error)
        ElMessage.error('同步代理位置失败，请检查网络连接')
      } finally {
        agentLocationsLoading.value = false
      }
    }
    
    // 代理位置分页方法
    const handleAgentLocationsSizeChange = (size) => {
      agentLocationsSize.value = size
      agentLocationsPage.value = 1 // 切换每页显示数量时重置到第一页
    }
    
    const handleAgentLocationsPageChange = (page) => {
      agentLocationsPage.value = page
    }

    // 获取位置绑定的代理信息
    const loadLocationAgents = async (locationArn) => {
      if (!selectedPayerId.value || !locationArn) return
      
      locationAgentsLoading.value = true
      try {
        const response = await getLocationAgents({
          payer_id: selectedPayerId.value,
          location_arn: locationArn
        })
        if (response.success) {
          locationAgentsList.value = response.data.agents || []
          if (locationAgentsList.value.length > 0) {
            console.log(`找到 ${locationAgentsList.value.length} 个绑定的代理`)
          } else {
            console.log('该位置未绑定任何代理')
          }
        } else {
          ElMessage.error(response.message || '获取位置代理失败')
          locationAgentsList.value = []
        }
      } catch (error) {
        console.error('获取位置代理失败:', error)
        ElMessage.error('获取位置代理失败，请检查网络连接')
        locationAgentsList.value = []
      } finally {
        locationAgentsLoading.value = false
      }
    }

    onUnmounted(() => {
      // 清理定时器
      stopAutoRefresh()
    })

    return {
      activeTab,
      selectedPayerId,
      selectedPayerName,
      locationList,
      locationLoading,
      locationPage,
      locationSize,
      locationTotal,
      taskList,
      taskLoading,
      taskPage,
      taskSize,
      taskTotal,
      executionList,
      executionLoading,
      executionPage,
      executionSize,
      executionTotal,
      syncLoading,
      syncExecutionsLoading,
      createLocationVisible,
      createLocationLoading,
      locationForm,
      locationRules,
      createTaskVisible,
      createTaskLoading,
      taskForm,
      taskRules,
      locationDetailVisible,
      taskDetailVisible,
      executionDetailVisible,
      selectedLocation,
      selectedTask,
      selectedExecution,
      onPayerChange,
      loadLocations,
      loadTasks,
      loadExecutions,
      syncFromAws,
      showCreateLocationDialog,
      createLocation,
      showCreateTaskDialog,
      createTask,
      startTask,
      getStatusTagType,
      getStatusDisplayText,
      isTaskRunning,
      getStartButtonText,
      getLocationTypeColor,
      formatBytes,
      handleLocationSizeChange,
      handleLocationCurrentChange,
      handleTaskSizeChange,
      handleTaskCurrentChange,
      handleExecutionSizeChange,
      handleExecutionCurrentChange,
      viewLocationDetail,
      viewTaskDetail,
      viewExecutionDetail,
      viewExecutions,
      startTaskFromDetail,
      syncExecutionStatusAction,
      syncAllRunningExecutionsAction,
      updateLocalAlias,
      startEdit,
      finishEdit,
      handleSortChange,
      agentList,
      agentTotal,
      agentLoading,
      agentQuery,
      currentAgent,
      agentDialogVisible,
      fetchAgentList,
      handleSyncAgents,
      handleViewDetails,
      handleRefreshStatus,
      getStatusType,
      handleAgentSizeChange,
      handleAgentPageChange,
      emptyText,
      // 代理位置相关
      agentLocationsDialogVisible,
      agentLocationsLoading,
      agentLocationsList,
      agentLocationsTotal,
      agentLocationsPage,
      agentLocationsSize,
      paginatedAgentLocations,
      currentAgentForLocations,
      handleViewAgentLocations,
      loadAgentLocations,
      handleSyncAgentLocations,
      handleAgentLocationsSizeChange,
      handleAgentLocationsPageChange,
      // 位置代理相关
      locationAgentsList,
      locationAgentsLoading,
      loadLocationAgents
    }
  }
}
</script>

<style scoped>
.datasync-manage {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  color: #303133;
}

.page-header p {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.payer-selector {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  align-items: center;
}

.payer-selector span {
  font-weight: 500;
  color: #606266;
}

.datasync-tabs {
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tab-content {
  padding: 20px 20px 0 20px;
}

.toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.pagination-container {
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.el-form-item {
  margin-bottom: 20px;
}

.uri-text {
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 12px;
}

.detail-content {
  max-height: 500px;
  overflow-y: auto;
}

.detail-content .el-descriptions {
  margin-top: 0;
}

.detail-content .el-descriptions-item__label {
  background-color: #fafafa !important;
  font-weight: 600;
  color: #606266;
}

.detail-content .el-descriptions-item__content {
  background-color: #fff !important;
}

.alias-cell {
  display: flex;
  align-items: center;
}

.alias-text {
  color: #606266;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.alias-text:empty::before {
  content: '设置别名';
  color: #909399;
}

.edit-icon {
  font-size: 14px;
  color: #909399;
  opacity: 0;
  transition: opacity 0.2s;
}

.alias-text:hover .edit-icon {
  opacity: 1;
}

.alias-text:hover {
  color: #409eff;
}

.agent-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.agent-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.agent-item .el-icon {
  color: #409eff;
}

.filter-container {
  margin-bottom: 20px;
}

/* 代理信息样式 */
.agent-section {
  min-height: 60px;
}

.no-agents {
  padding: 20px;
  text-align: center;
  color: #909399;
}

.agents-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.agent-item {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.agent-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}

.agent-card {
  padding: 12px;
}

.agent-header {
  margin-bottom: 8px;
}

.agent-name {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-weight: 500;
}

.agent-name .name {
  color: #303133;
  font-size: 14px;
}

.agent-arn {
  padding-left: 24px;
}

.agent-details {
  padding-left: 24px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 12px;
}

.detail-row .label {
  color: #909399;
  min-width: 60px;
}

.detail-row .value {
  color: #606266;
}
</style> 